import { useState } from 'react';
import type { KeyedMutator } from 'swr';
import { Loader2 } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useCommunity } from '@/hooks/use-community';
import { communityHasAvenues } from '@/lib/community-platform-tags';
import {
  SegmentedTabs,
  SegmentedTabsContent,
  SegmentedTabsList,
  SegmentedTabsTrigger,
} from '@/components/ui/segmented-tabs';
import type {
  Community,
  ProfileFieldConfig,
  OnboardingFormConfig,
  NavigationConfig,
} from '@/types';
import { GeneralSettings } from './components/GeneralSettings';
import { FeatureSettings } from './components/FeatureSettings';
import { ProfileFieldSettings } from './components/ProfileFieldSettings';
import { PrivacySettings } from './components/PrivacySettings';
import { OnboardingSettings } from './components/OnboardingSettings';
import { AvenueSettings } from './components/AvenueSettings';
import { MembershipSettings } from './components/MembershipSettings';
import { PointsSettings } from './components/PointsSettings';
import { RoleAccessSettings } from './components/RoleAccessSettings';

type CommunityMutate = KeyedMutator<{ community: Community }>;

function CommunitySettingsPanels({
  communityId,
  community,
  mutateCommunity,
}: {
  communityId: string;
  community: Community;
  mutateCommunity: CommunityMutate;
}) {
  const [name, setName] = useState(() => community.name || '');
  const [slug, setSlug] = useState(() => community.slug || '');
  const [logo, setLogo] = useState(() => community.logo || '');
  const [banner, setBanner] = useState(() => community.banner || '');
  const [customPrivacy, setCustomPrivacy] = useState<NonNullable<Community['customPrivacy']>>(
    () => community.customPrivacy || 'OPEN'
  );
  const [requiresApproval, setRequiresApproval] = useState(() =>
    Boolean(community.requiresApproval)
  );
  const [inviteCodeEnabled, setInviteCodeEnabled] = useState(
    () => community.inviteCodeEnabled !== false
  );
  const [searchVisible, setSearchVisible] = useState(() => community.searchVisible ?? true);

  const [isBusinessEnabled, setIsBusinessEnabled] = useState(
    () => community.isBusinessEnabled || false
  );
  const [isBusinessDirectoryEnabled, setIsBusinessDirectoryEnabled] = useState(
    () => community.isBusinessDirectoryEnabled || false
  );
  const [isGivesAsksEnabled, setIsGivesAsksEnabled] = useState(() => Boolean(community.isGivesAsksEnabled));
  const [isLeadEnabled, setIsLeadEnabled] = useState(() => Boolean(community.isLeadEnabled));
  const [isTestimonialEnabled, setIsTestimonialEnabled] = useState(() =>
    Boolean(community.isTestimonialEnabled)
  );
  const [isThankYouNoteEnabled, setIsThankYouNoteEnabled] = useState(() =>
    Boolean(community.isThankYouNoteEnabled)
  );
  const [isNotesEnabled, setIsNotesEnabled] = useState(() =>
    community.isNotesEnabled === undefined ? true : Boolean(community.isNotesEnabled)
  );
  const [enableSpouse, setEnableSpouse] = useState(() => Boolean(community.enableSpouse));
  const [isMeetupEnabled, setIsMeetupEnabled] = useState(() => Boolean(community.isMeetupEnabled));
  const [isProjectsEnabled, setIsProjectsEnabled] = useState(() =>
    Boolean(community.isProjectsEnabled)
  );
  const [isProjectDashboardEnabled, setIsProjectDashboardEnabled] = useState(() =>
    Boolean(community.isProjectDashboardEnabled)
  );
  const [isPointsEnabled, setIsPointsEnabled] = useState(() => Boolean(community.isPointsEnabled));
  const [onboardingFormEnabled, setOnboardingFormEnabled] = useState(() =>
    Boolean(community.onboardingFormEnabled)
  );
  const [showUpcomingEventsInSocialDashboard, setShowUpcomingEventsInSocialDashboard] = useState(
    () => community.showUpcomingEventsInSocialDashboard ?? true
  );
  const [showCelebrations, setShowCelebrations] = useState(
    () => community.showCelebrations ?? false
  );
  const [isPersonalizedSocialFeedEnabled, setIsPersonalizedSocialFeedEnabled] = useState(
    () => community.isPersonalizedSocialFeedEnabled ?? true
  );

  const [controlledBusinessListing, setControlledBusinessListing] = useState(() =>
    Boolean(community.controlledBusinessListing)
  );
  const [businessLinkApprovalRequired, setBusinessLinkApprovalRequired] = useState(() =>
    Boolean(community.businessLinkApprovalRequired)
  );
  const [isAdvertisementEnabled, setIsAdvertisementsEnabled] = useState(() =>
    Boolean(community.isAdvertisementEnabled)
  );
  const [businessLinksMinPerMember, setBusinessLinksMinPerMember] = useState<number | null>(
    () => community.businessLinksMinPerMember ?? null
  );
  const [businessLinksMaxPerMember, setBusinessLinksMaxPerMember] = useState<number | null>(
    () => community.businessLinksMaxPerMember ?? null
  );
  const businessLinkPolicyVersion = community.businessLinkPolicyVersion ?? 0;
  const [businessDirectoryTitle, setBusinessDirectoryTitle] = useState(
    () => community.businessDirectoryTitle ?? ''
  );
  const [businessDirectoryPublic, setBusinessDirectoryPublic] = useState(() =>
    Boolean(community.businessDirectoryPublic)
  );
  const [businessDirectoryShowAddBusiness, setBusinessDirectoryShowAddBusiness] = useState(() =>
    Boolean(community.businessDirectoryShowAddBusiness)
  );

  const [onboardingForm, setOnboardingForm] = useState<OnboardingFormConfig | null>(
    () => community.onboardingForm || null
  );
  const [profileFieldConfig, setProfileFieldConfig] = useState<ProfileFieldConfig>(
    () => community.profileFieldConfig || {}
  );
  const [navigationConfig, setNavigationConfig] = useState<NavigationConfig | null>(
    () => community.navigationConfig || null
  );

  const [originalSlug, setOriginalSlug] = useState(() => community.slug || '');
  const [inviteLink, setInviteLink] = useState(community.inviteLink || '');
  const [inviteCode, setInviteCode] = useState(community.inviteCode || '');
  const [isMembershipManagementEnabled, setIsMembershipManagementEnabled] = useState(() =>
    Boolean(community.isMembershipManagementEnabled)
  );

  const isAvenuesEnabled = communityHasAvenues(community.platformTags);

  return (
    <div className="container mx-auto py-10 px-4 max-w-5xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Community Settings</h1>
          <p className="text-muted-foreground mt-2">
            Manage your community profile, privacy, and features.
          </p>
        </div>
      </div>

      <SegmentedTabs defaultValue="general" className="space-y-6">
        <SegmentedTabsList variant="compact" className="w-full">
          <SegmentedTabsTrigger variant="compact" value="general">
            General
          </SegmentedTabsTrigger>
          <SegmentedTabsTrigger variant="compact" value="features">
            Features
          </SegmentedTabsTrigger>
          {isPointsEnabled && (
            <SegmentedTabsTrigger variant="compact" value="points">
              Points
            </SegmentedTabsTrigger>
          )}
          <SegmentedTabsTrigger variant="compact" value="membership">
            Membership
          </SegmentedTabsTrigger>
          <SegmentedTabsTrigger variant="compact" value="profile-fields">
            Profile Fields
          </SegmentedTabsTrigger>
          <SegmentedTabsTrigger variant="compact" value="onboarding">
            Additional details
          </SegmentedTabsTrigger>
          <SegmentedTabsTrigger variant="compact" value="privacy">
            Privacy
          </SegmentedTabsTrigger>
          {isProjectsEnabled && isAvenuesEnabled && (
            <SegmentedTabsTrigger variant="compact" value="avenues">
              Avenues
            </SegmentedTabsTrigger>
          )}
          <SegmentedTabsTrigger variant="compact" value="roles">
            Roles
          </SegmentedTabsTrigger>
        </SegmentedTabsList>

        <SegmentedTabsContent value="general">
          <GeneralSettings
            communityId={communityId}
            mutateCommunity={mutateCommunity}
            name={name}
            setName={setName}
            slug={slug}
            setSlug={setSlug}
            logo={logo}
            setLogo={setLogo}
            banner={banner}
            setBanner={setBanner}
            originalSlug={originalSlug}
            setOriginalSlug={setOriginalSlug}
            navigationConfig={navigationConfig}
            setNavigationConfig={setNavigationConfig}
            inviteLink={inviteLink}
            setInviteLink={setInviteLink}
            inviteCode={inviteCode}
            setInviteCode={setInviteCode}
            inviteCodeEnabled={inviteCodeEnabled}
            themeColors={(community.themeColors as { primary?: string | null } | null) ?? null}
          />
        </SegmentedTabsContent>

        <SegmentedTabsContent value="features">
          <FeatureSettings
            communityId={communityId}
            mutateCommunity={mutateCommunity}
            isBusinessEnabled={isBusinessEnabled}
            setIsBusinessEnabled={setIsBusinessEnabled}
            isBusinessDirectoryEnabled={isBusinessDirectoryEnabled}
            setIsBusinessDirectoryEnabled={setIsBusinessDirectoryEnabled}
            isGivesAsksEnabled={isGivesAsksEnabled}
            setIsGivesAsksEnabled={setIsGivesAsksEnabled}
            isLeadEnabled={isLeadEnabled}
            setIsLeadEnabled={setIsLeadEnabled}
            isTestimonialEnabled={isTestimonialEnabled}
            setIsTestimonialEnabled={setIsTestimonialEnabled}
            isThankYouNoteEnabled={isThankYouNoteEnabled}
            setIsThankYouNoteEnabled={setIsThankYouNoteEnabled}
            isNotesEnabled={isNotesEnabled}
            setIsNotesEnabled={setIsNotesEnabled}
            enableSpouse={enableSpouse}
            setEnableSpouse={setEnableSpouse}
            isMeetupEnabled={isMeetupEnabled}
            setIsMeetupEnabled={setIsMeetupEnabled}
            isProjectsEnabled={isProjectsEnabled}
            setIsProjectsEnabled={setIsProjectsEnabled}
            isProjectDashboardEnabled={isProjectDashboardEnabled}
            setIsProjectDashboardEnabled={setIsProjectDashboardEnabled}
            isPointsEnabled={isPointsEnabled}
            setIsPointsEnabled={setIsPointsEnabled}
            onboardingFormEnabled={onboardingFormEnabled}
            setOnboardingFormEnabled={setOnboardingFormEnabled}
            showUpcomingEventsInSocialDashboard={showUpcomingEventsInSocialDashboard}
            setShowUpcomingEventsInSocialDashboard={setShowUpcomingEventsInSocialDashboard}
            showCelebrations={showCelebrations}
            setShowCelebrations={setShowCelebrations}
            isPersonalizedSocialFeedEnabled={isPersonalizedSocialFeedEnabled}
            setIsPersonalizedSocialFeedEnabled={setIsPersonalizedSocialFeedEnabled}
            controlledBusinessListing={controlledBusinessListing}
            setControlledBusinessListing={setControlledBusinessListing}
            businessLinkApprovalRequired={businessLinkApprovalRequired}
            setBusinessLinkApprovalRequired={setBusinessLinkApprovalRequired}
            businessLinksMinPerMember={businessLinksMinPerMember}
            setBusinessLinksMinPerMember={setBusinessLinksMinPerMember}
            businessLinksMaxPerMember={businessLinksMaxPerMember}
            setBusinessLinksMaxPerMember={setBusinessLinksMaxPerMember}
            businessLinkPolicyVersion={businessLinkPolicyVersion}
            isAdvertisementEnabled={isAdvertisementEnabled}
            setIsAdvertisementEnabled={setIsAdvertisementsEnabled}
            businessDirectoryTitle={businessDirectoryTitle}
            setBusinessDirectoryTitle={setBusinessDirectoryTitle}
            businessDirectoryPublic={businessDirectoryPublic}
            setBusinessDirectoryPublic={setBusinessDirectoryPublic}
            businessDirectoryShowAddBusiness={businessDirectoryShowAddBusiness}
            setBusinessDirectoryShowAddBusiness={setBusinessDirectoryShowAddBusiness}
          />
        </SegmentedTabsContent>

        {isPointsEnabled && (
          <SegmentedTabsContent value="points">
            <PointsSettings
              communityId={communityId}
              pointsConfig={community.pointsConfig}
              mutateCommunity={mutateCommunity}
            />
          </SegmentedTabsContent>
        )}

        <SegmentedTabsContent value="membership">
          <MembershipSettings
            communityId={communityId}
            mutateCommunity={mutateCommunity}
            isMembershipManagementEnabled={isMembershipManagementEnabled}
            setIsMembershipManagementEnabled={setIsMembershipManagementEnabled}
          />
        </SegmentedTabsContent>

        <SegmentedTabsContent value="profile-fields">
          <ProfileFieldSettings
            communityId={communityId}
            mutateCommunity={mutateCommunity}
            profileFieldConfig={profileFieldConfig}
            setProfileFieldConfig={setProfileFieldConfig}
          />
        </SegmentedTabsContent>

        <SegmentedTabsContent value="onboarding">
          <OnboardingSettings
            communityId={communityId}
            mutateCommunity={mutateCommunity}
            onboardingFormEnabled={onboardingFormEnabled}
            setOnboardingFormEnabled={setOnboardingFormEnabled}
            onboardingForm={onboardingForm}
            setOnboardingForm={setOnboardingForm}
            requiresApproval={requiresApproval}
          />
        </SegmentedTabsContent>

        <SegmentedTabsContent value="privacy">
          <PrivacySettings
            communityId={communityId}
            mutateCommunity={mutateCommunity}
            customPrivacy={customPrivacy}
            setCustomPrivacy={setCustomPrivacy}
            requiresApproval={requiresApproval}
            setRequiresApproval={setRequiresApproval}
            inviteCodeEnabled={inviteCodeEnabled}
            setInviteCodeEnabled={setInviteCodeEnabled}
            searchVisible={searchVisible}
            setSearchVisible={setSearchVisible}
          />
        </SegmentedTabsContent>

        {isProjectsEnabled && isAvenuesEnabled && (
          <SegmentedTabsContent value="avenues">
            <AvenueSettings communityId={communityId} />
          </SegmentedTabsContent>
        )}

        <SegmentedTabsContent value="roles">
          <RoleAccessSettings communityId={communityId} />
        </SegmentedTabsContent>
      </SegmentedTabs>
    </div>
  );
}

export default function CommunitySettingsPage() {
  const { communityId } = useParams<{ communityId: string }>();
  const {
    community: communityPayload,
    mutate: mutateCommunity,
    isLoading,
  } = useCommunity(communityId);

  if (!communityId) return null;

  const community = communityPayload?.community;

  if (isLoading || !community) {
    return (
      <div className="container mx-auto py-16 flex flex-col items-center justify-center gap-3 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm font-medium">Loading community settings…</p>
      </div>
    );
  }

  return (
    <CommunitySettingsPanels
      key={`${communityId}:${community.updatedAt}`}
      communityId={communityId}
      community={community}
      mutateCommunity={mutateCommunity}
    />
  );
}
